/**
 * The favorite hearts (blocks/birrot-favorite.liquid; docs/plans/completed/FAVORITES.md).
 *
 * Every page with a heart is rendered with the customer's list (Liquid), so
 * the script reads nothing on load: it applies the whole list from
 * snippets/birrot-favorites-data.liquid (the hearts' own Liquid sees the
 * first 50 only), and on a click toggles the heart at once, sends the
 * change to the app proxy (/apps/favorites, apps/birrot-favorites/), and
 * applies the list the proxy answers with to every heart on the page. A
 * failed change is reverted, with a short message by the heart.
 *
 * Changes go out one at a time: the proxy reads the list, changes it and
 * writes it back, so two at once could each undo the other. What the page
 * shows is the last list the proxy confirmed (`base`) with the changes
 * still on their way (`pending`) laid over it, so an answer to an earlier
 * click never undoes a later one for the round trip in between.
 *
 * A guest's click opens the account sheet (the header's <shopify-account>)
 * or, where it is not on show, goes to the login page, which brings the
 * shopper back here afterwards (`return_url`). So does a customer's click
 * once the proxy answers 401: the session has ended since the page was
 * rendered.
 */

import { Component } from '@theme/component';

const CHANGE = 'birrot:favorites-change';
const ERROR_MS = 4000;

/**
 * @typedef {object} FavoritesData
 * @property {boolean} customer
 * @property {string[]} ids
 * @property {string} proxy
 * @property {string} login
 * @property {string} error
 */

/** @typedef {{ id: string, add: boolean }} Change */

/** @returns {FavoritesData} */
function readData() {
  const fallback = { customer: false, ids: [], proxy: '/apps/favorites', login: '/account/login', error: '' };
  const element = document.getElementById('birrot-favorites-data');
  if (!element?.textContent) return fallback;
  try {
    return { ...fallback, ...JSON.parse(element.textContent) };
  } catch {
    return fallback;
  }
}

const data = readData();

/** The list as the proxy last confirmed it (the page's render, to begin with). */
let base = data.ids.map(String);

/** The changes sent or waiting to be sent, oldest first. @type {Change[]} */
const pending = [];

/** The list as this page shows it: `base` with `pending` laid over it. */
let saved = new Set(base);

/** The chain the changes queue on. */
let queue = Promise.resolve();

/**
 * @param {string[]} list
 * @param {Change[]} changes
 * @returns {string[]}
 */
function overlay(list, changes) {
  let result = list;
  for (const { id, add } of changes) {
    result = add ? (result.includes(id) ? result : [...result, id]) : result.filter((other) => other !== id);
  }
  return result;
}

/** Tell every heart the list as it stands now. */
function publish() {
  saved = new Set(overlay(base, pending));
  document.dispatchEvent(new CustomEvent(CHANGE, { detail: { ids: [...saved] } }));
}

/** A reply the proxy refused; `status` is its HTTP status. */
class ProxyError extends Error {
  /** @param {number} status @param {string} message */
  constructor(status, message) {
    super(message);
    this.status = status;
  }
}

/**
 * @param {'GET' | 'POST'} method
 * @param {object} [body]
 * @returns {Promise<string[]>}
 */
async function request(method, body) {
  const response = await fetch(data.proxy, {
    method,
    headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
    body: body ? JSON.stringify(body) : undefined,
    credentials: 'same-origin',
  });
  const json = await response.json().catch(() => null);
  if (!response.ok || !Array.isArray(json?.ids)) {
    throw new ProxyError(response.status, json?.error || `Favorites answered ${response.status}`);
  }
  return json.ids.map(String);
}

/**
 * A guest's click: the account sheet if it is on show, else the login
 * page, with this page as the place to come back to.
 */
function logIn() {
  const account = /** @type {HTMLElement | null} */ (document.querySelector('shopify-account'));
  const button = account?.shadowRoot?.querySelector('button');
  if (account && button && account.checkVisibility?.()) {
    button.click();
    return;
  }
  const url = new URL(data.login, window.location.href);
  url.searchParams.set('return_url', window.location.pathname + window.location.search);
  window.location.assign(url.href);
}

/**
 * The proxy says no customer is signed in: the page was rendered for one,
 * but the session has ended since. Every heart empties, and the next
 * click goes to log in.
 */
function signedOut() {
  data.customer = false;
  base = [];
  pending.length = 0;
  publish();
}

class BirrotFavorite extends Component {
  /** @type {number | undefined} */
  #errorTimer;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CHANGE, this.#handleChange);
    if (data.customer) this.#show(saved.has(this.productId));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CHANGE, this.#handleChange);
    window.clearTimeout(this.#errorTimer);
  }

  get productId() {
    return this.dataset.productId ?? '';
  }

  /** @param {Event} [event] */
  toggle(event) {
    event?.preventDefault();
    event?.stopPropagation();
    if (!data.customer) {
      logIn();
      return;
    }

    /** @type {Change} */
    const change = { id: this.productId, add: !saved.has(this.productId) };
    pending.push(change);
    publish();

    queue = queue
      .then(async () => {
        if (!pending.includes(change)) return; // Dropped by a sign-out meanwhile.
        this.toggleAttribute('data-pending', true);
        try {
          const ids = await request('POST', change.add ? { add: [change.id] } : { remove: [change.id] });
          if (!pending.includes(change)) return;
          base = ids;
          pending.splice(pending.indexOf(change), 1);
          publish();
        } catch (error) {
          console.warn('[birrot-favorites]', error);
          if (!pending.includes(change)) return;
          // Without this change: the later clicks stay as they are.
          pending.splice(pending.indexOf(change), 1);
          if (error instanceof ProxyError && error.status === 401) {
            signedOut();
            logIn();
            return;
          }
          publish();
          this.#showError();
        } finally {
          this.removeAttribute('data-pending');
        }
      })
      .catch(() => {});
  }

  /** @param {Event} event */
  #handleChange = (event) => {
    const ids = /** @type {CustomEvent<{ ids: string[] }>} */ (event).detail.ids;
    this.#show(ids.includes(this.productId));
  };

  /** @param {boolean} isSaved */
  #show(isSaved) {
    this.toggleAttribute('data-saved', isSaved);
    const button = this.querySelector('.birrot-favorite__button');
    if (!button) return;
    button.setAttribute('aria-pressed', String(isSaved));
    const label = isSaved ? button.getAttribute('data-label-remove') : button.getAttribute('data-label-add');
    if (label) button.setAttribute('aria-label', label);
  }

  #showError() {
    if (!data.error) return;
    let message = this.querySelector('.birrot-favorite__error');
    if (!message) {
      message = document.createElement('span');
      message.className = 'birrot-favorite__error';
      message.setAttribute('role', 'status');
      this.append(message);
    }
    message.textContent = data.error;
    window.clearTimeout(this.#errorTimer);
    this.#errorTimer = window.setTimeout(() => message?.remove(), ERROR_MS);
  }
}

if (!customElements.get('birrot-favorite')) {
  customElements.define('birrot-favorite', BirrotFavorite);
}

// The favorites page's log-in link for a guest (sections/birrot-favorites.liquid)
// opens the account sheet as a heart does; without the script it is a link.
document.addEventListener('click', (event) => {
  const link = event.target instanceof Element ? event.target.closest('.birrot-favorites__login') : null;
  if (!link) return;
  event.preventDefault();
  logIn();
});

// Back from the back-forward cache, the page is as it was left: ask the
// proxy for the list, as the header's cart count asks /cart.js.
window.addEventListener('pageshow', (event) => {
  if (!event.persisted || !data.customer) return;
  queue = queue
    .then(async () => {
      base = await request('GET');
      publish();
    })
    .catch((error) => {
      console.warn('[birrot-favorites]', error);
      if (error instanceof ProxyError && error.status === 401) signedOut();
    });
});
